import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Send, 
  Plus,
  Users,
  FileText,
  Clock,
  CheckCircle2,
  Loader2,
  Eye,
  Play,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: "bg-zinc-100 text-zinc-700",
  scheduled: "bg-blue-50 text-blue-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700"
};

const CampaignsSection = ({ password }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [sendConfirmDialog, setSendConfirmDialog] = useState(null);
  const [isSending, setIsSending] = useState(false);
  
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    template_id: "",
    filters: {
      paid: "",
      outcome: ""
    }
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [campaignsRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/campaigns?password=${password}`),
        axios.get(`${API_URL}/admin/templates?password=${password}`)
      ]);
      setCampaigns(campaignsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kon data niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [password]);

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.template_id) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    try {
      // Build filters object
      const filters = {};
      if (newCampaign.filters.paid !== "") {
        filters.paid = newCampaign.filters.paid === "true";
      }
      if (newCampaign.filters.outcome) {
        filters.outcome = newCampaign.filters.outcome;
      }

      await axios.post(`${API_URL}/admin/campaigns?password=${password}`, {
        name: newCampaign.name,
        template_id: newCampaign.template_id,
        filters
      });
      
      toast.success("Campaign aangemaakt");
      setCreateDialog(false);
      setNewCampaign({ name: "", template_id: "", filters: { paid: "", outcome: "" } });
      fetchData();
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast.error("Kon campaign niet aanmaken");
    }
  };

  const handlePreviewCampaign = async (campaign) => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/campaigns/${campaign.id}/preview?password=${password}`
      );
      setPreviewData(response.data);
      setPreviewDialog(campaign);
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Kon preview niet laden");
    }
  };

  const handleSendCampaign = async (campaign) => {
    setIsSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/campaigns/${campaign.id}/send?password=${password}`
      );
      
      if (response.data.status === "completed") {
        toast.success(`Campaign verzonden: ${response.data.emails_sent} emails verstuurd`);
        setSendConfirmDialog(null);
        fetchData();
      } else {
        toast.error(response.data.message || "Verzenden mislukt");
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error("Kon campaign niet verzenden");
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-lg font-medium text-zinc-900">Email Campaigns</h2>
          <p className="text-sm text-zinc-500">Verstuur gerichte emails naar segmenten</p>
        </div>
        <Button 
          onClick={() => setCreateDialog(true)}
          className="rounded-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe Campaign
        </Button>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden">
        {campaigns.length === 0 ? (
          <div className="text-center py-16">
            <Send className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">Nog geen campaigns</p>
            <Button
              variant="outline"
              onClick={() => setCreateDialog(true)}
              className="mt-4 rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Eerste Campaign Aanmaken
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="font-medium">Naam</TableHead>
                <TableHead className="font-medium">Template</TableHead>
                <TableHead className="font-medium">Status</TableHead>
                <TableHead className="font-medium">Verstuurd</TableHead>
                <TableHead className="font-medium">Datum</TableHead>
                <TableHead className="font-medium w-40">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => {
                const template = templates.find(t => t.id === campaign.template_id);
                
                return (
                  <TableRow key={campaign.id} className="hover:bg-zinc-50">
                    <TableCell className="font-medium text-zinc-900">
                      {campaign.name}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      {template?.name || campaign.template_id}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[campaign.status]}`}>
                        {campaign.status === "draft" ? "Concept" : 
                         campaign.status === "sent" ? "Verzonden" : 
                         campaign.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      {campaign.status === "sent" ? `${campaign.emails_sent} emails` : "-"}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">
                      {campaign.sent_at ? formatDate(campaign.sent_at) : formatDate(campaign.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewCampaign(campaign)}
                          className="rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {campaign.status === "draft" && (
                          <Button
                            size="sm"
                            onClick={() => setSendConfirmDialog(campaign)}
                            className="rounded-lg bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Verstuur
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Campaign Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe Campaign</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Campaign Naam</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="Bijv. Herinnering onbetaalde leads"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Email Template</Label>
              <Select
                value={newCampaign.template_id}
                onValueChange={(v) => setNewCampaign({...newCampaign, template_id: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecteer template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-t border-zinc-200 pt-4">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Doelgroep Filters
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Select
                  value={newCampaign.filters.paid || "all"}
                  onValueChange={(v) => setNewCampaign({
                    ...newCampaign, 
                    filters: {...newCampaign.filters, paid: v === "all" ? "" : v}
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Betaalstatus" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="true">Betaald</SelectItem>
                    <SelectItem value="false">Onbetaald</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select
                  value={newCampaign.filters.outcome || "all"}
                  onValueChange={(v) => setNewCampaign({
                    ...newCampaign,
                    filters: {...newCampaign.filters, outcome: v === "all" ? "" : v}
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Risico niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="laag_risico">Laag risico</SelectItem>
                    <SelectItem value="twijfelgeval">Twijfelgeval</SelectItem>
                    <SelectItem value="verhoogd_risico">Verhoogd risico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleCreateCampaign}
              className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewDialog} onOpenChange={() => setPreviewDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Campaign Preview</DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-500">Doelgroep</span>
                  <span className="text-lg font-medium text-zinc-900">{previewData.total_leads} leads</span>
                </div>
                <p className="text-xs text-zinc-500">
                  Template: {previewData.template?.name}
                </p>
              </div>
              
              {previewData.sample_leads?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-zinc-700 mb-2">Voorbeeld ontvangers:</p>
                  <div className="space-y-2">
                    {previewData.sample_leads.slice(0, 3).map((lead, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm bg-zinc-50 rounded-lg p-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                          {(lead.name?.[0] || lead.email?.[0] || "?").toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{lead.name || "Onbekend"}</p>
                          <p className="text-zinc-500 text-xs">{lead.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog(null)}>
              Sluiten
            </Button>
            {previewDialog?.status === "draft" && (
              <Button 
                onClick={() => {
                  setPreviewDialog(null);
                  setSendConfirmDialog(previewDialog);
                }}
                className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
              >
                <Send className="w-4 h-4 mr-2" />
                Versturen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={!!sendConfirmDialog} onOpenChange={() => setSendConfirmDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Campaign Versturen?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-zinc-600">
              Weet je zeker dat je de campaign "{sendConfirmDialog?.name}" wilt versturen?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Let op:</strong> Emails worden direct verzonden en kunnen niet worden teruggehaald.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendConfirmDialog(null)}>
              Annuleren
            </Button>
            <Button 
              onClick={() => handleSendCampaign(sendConfirmDialog)}
              disabled={isSending}
              className="bg-gradient-to-r from-[#1DA1F2] to-[#2563EB]"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Ja, Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsSection;
